import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  ShoppingCart,
  ArrowRight,
  Award,
  Users,
  Sparkles,
  CheckCircle2,
  ExternalLink,
} from "lucide-react";
import SectionHeader from "../components/site/SectionHeader";
import SafeImage from "../components/site/SafeImage";
import { CLINIC, waLink } from "../lib/clinic";
import { BOOK_SQUINT } from "../lib/books";
import {
  SITE,
  REF,
  breadcrumb,
  graph,
  CLINIC_NODE,
} from "../lib/schema";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";

// Route: /book/squint-simplified
// Purpose: authoritative landing page for the book "Squint Simplified — A
// Complete Guide for Patients" by Dr. Manisha Mishra (Author) and
// Dr. Shantanu Kumar Gupta (Editor). Doubles as an E-E-A-T anchor for
// squint / strabismus / paediatric-ophthalmology queries.
const BookSquintSimplified = () => {
  const b = BOOK_SQUINT;
  const pageUrl = `${SITE}/book/${b.slug}`;
  const coverAbs = `${SITE}${b.coverImageHiRes}`;

  usePageMeta({
    title: `${b.fullTitle} — Book by Dr. Manisha Mishra`,
    path: `/book/${b.slug}`,
    description: `${b.synopsisShort} Now available on Amazon (ASIN ${b.asin}). Edited by Dr. Shantanu Kumar Gupta.`,
  });

  const jsonLd = useMemo(
    () =>
      graph([
        breadcrumb([
          { name: "Home", path: "/" },
          { name: "Books", path: `/book/${b.slug}` },
          { name: b.title, path: `/book/${b.slug}` },
        ]),
        {
          "@type": "Book",
          "@id": `${pageUrl}#book`,
          name: b.fullTitle,
          alternateName: b.title,
          headline: b.fullTitle,
          bookFormat: "https://schema.org/Paperback",
          numberOfPages: b.pages,
          inLanguage: "en",
          isbn: b.isbn13,
          identifier: [
            { "@type": "PropertyValue", propertyID: "ISBN-13", value: b.isbn13 },
            { "@type": "PropertyValue", propertyID: "ASIN", value: b.asin },
          ],
          datePublished: b.publicationDate,
          publisher: { "@type": "Organization", name: b.publisher },
          author: {
            "@type": "Person",
            "@id": "https://drshantanugupta.com/#physician-manisha",
            name: b.author.name,
            url: `${SITE}${b.author.url}`,
          },
          editor: {
            "@type": "Person",
            "@id": REF.physician,
            name: b.editor.name,
            url: `${SITE}${b.editor.url}`,
          },
          about: [
            { "@type": "MedicalCondition", name: "Strabismus" },
            { "@type": "MedicalCondition", name: "Squint" },
            { "@type": "MedicalCondition", name: "Amblyopia" },
            { "@type": "Thing", name: "Pediatric Ophthalmology" },
          ],
          audience: {
            "@type": "PeopleAudience",
            audienceType: "Patients, parents and families affected by squint (strabismus)",
          },
          image: [coverAbs],
          url: pageUrl,
          workExample: {
            "@type": "Book",
            "@id": `${pageUrl}#book-paperback`,
            bookFormat: "https://schema.org/Paperback",
            isbn: b.isbn13,
            inLanguage: "en",
            potentialAction: {
              "@type": "ReadAction",
              target: [b.amazonUrl],
              expectsAcceptanceOf: {
                "@type": "Offer",
                price: b.priceUSD,
                priceCurrency: "USD",
                availability: "https://schema.org/InStock",
                url: b.amazonUrl,
              },
            },
          },
          offers: {
            "@type": "Offer",
            url: b.amazonUrl,
            price: b.priceUSD,
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            seller: { "@type": "Organization", name: "Amazon" },
          },
          sameAs: [b.amazonUrl],
          publisherImprint: b.publisher,
          copyrightYear: 2026,
          license: b.amazonUrl,
        },
        // ProfilePage-like WebPage node so Google links this page to the
        // MedicalClinic and Physician entities.
        {
          "@type": "WebPage",
          "@id": `${pageUrl}#webpage`,
          url: pageUrl,
          name: `${b.fullTitle} — Book by Dr. Manisha Mishra`,
          isPartOf: { "@id": REF.website },
          about: { "@id": `${pageUrl}#book` },
          primaryImageOfPage: { "@type": "ImageObject", url: coverAbs },
          speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".speakable"] },
        },
        CLINIC_NODE,
      ]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  usePageJsonLd(jsonLd);

  return (
    <div data-testid="book-squint-page">
      {/* HERO */}
      <section className="pt-20 pb-12 container-page">
        <nav aria-label="Breadcrumb" className="mb-6">
          <ol className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
            <li><Link to="/" className="hover:text-[hsl(var(--primary))]">Home</Link></li>
            <li>›</li>
            <li><Link to="/team/dr-manisha-mishra" className="hover:text-[hsl(var(--primary))]">Dr. Manisha Mishra</Link></li>
            <li>›</li>
            <li className="text-[hsl(var(--foreground))]/80">{b.title}</li>
          </ol>
        </nav>

        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-12 items-start">
          {/* Cover */}
          <div className="relative">
            <div className="absolute -inset-6 bg-gradient-to-br from-[hsl(var(--primary))]/10 via-transparent to-[hsl(var(--accent))]/10 rounded-[2.5rem] blur-2xl -z-10" aria-hidden="true" />
            <div className="relative overflow-hidden rounded-3xl border border-border/70 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.35)] max-w-md mx-auto aspect-[2/3]">
              <SafeImage
                src={b.coverImage}
                alt={`Cover of ${b.fullTitle} — a patient guide to squint (strabismus) by Dr. Manisha Mishra`}
                label={b.title}
                loading="eager"
                className="w-full h-full"
                data-testid="book-cover"
              />
            </div>
            <div className="mt-5 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              <BookOpen size={13} strokeWidth={1.8} />
              <span>{b.pages} pages · {b.format} · Published {new Date(b.publicationDate).toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</span>
            </div>
          </div>

          {/* Details */}
          <div>
            <div className="overline mb-4" data-testid="book-overline">Now Available · Amazon Books</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance" data-testid="book-h1">
              {b.title}
            </h1>
            <p className="mt-3 text-xl sm:text-2xl font-serif text-[hsl(var(--primary))] leading-snug">
              {b.subtitle}
            </p>

            <div className="mt-6 space-y-1 text-[15px]">
              <div>
                <span className="text-muted-foreground text-sm">Author · </span>
                <Link to={b.author.url} className="font-medium hover:text-[hsl(var(--primary))]" data-testid="book-author-link">
                  {b.author.name}
                </Link>
              </div>
              <div>
                <span className="text-muted-foreground text-sm">Editor · </span>
                <Link to={b.editor.url} className="font-medium hover:text-[hsl(var(--primary))]" data-testid="book-editor-link">
                  {b.editor.name}
                </Link>
              </div>
              <div className="text-sm text-muted-foreground pt-1">
                Published by {b.publisher} · ISBN {b.isbn13} · ASIN {b.asin}
              </div>
            </div>

            <p className="mt-6 max-w-2xl prose-elegant">
              {b.synopsisShort}
            </p>

            {/* CTAs */}
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={b.amazonUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                data-testid="book-buy-amazon-btn"
              >
                <ShoppingCart size={16} /> Buy on Amazon
                <ExternalLink size={13} className="opacity-70" />
              </a>
              <a
                href={waLink(`Hello, I would like to know more about the book "${b.fullTitle}" by Dr. Manisha Mishra.`)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost"
                data-testid="book-whatsapp-btn"
              >
                Ask on WhatsApp
              </a>
              <Link to="/team/dr-manisha-mishra" className="btn-ghost" data-testid="book-meet-author-btn">
                Meet the author
              </Link>
            </div>

            {/* Highlights */}
            <div className="mt-10 grid sm:grid-cols-3 gap-4" data-testid="book-highlights">
              {[
                { icon: Award, title: "Written by an MAMC-trained specialist", subtitle: "Squint & paediatric ophthalmology consultant" },
                { icon: Users, title: "Plain, patient-friendly language", subtitle: "No medical jargon — designed for families" },
                { icon: Sparkles, title: "Myths busted with evidence", subtitle: "Replaces WhatsApp forwards with clarity" },
              ].map(({ icon: Icon, title, subtitle }) => (
                <div key={title} className="p-4 rounded-2xl border border-border bg-card/60 dark:bg-white/[0.03]">
                  <Icon size={18} strokeWidth={1.5} className="text-[hsl(var(--accent))]" />
                  <div className="mt-2 font-serif text-[15px] leading-snug">{title}</div>
                  <div className="text-xs text-muted-foreground mt-1">{subtitle}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT THIS BOOK */}
      <section className="py-14 container-page max-w-3xl" data-testid="book-about">
        <SectionHeader overline="About this book" title="Why we wrote it" />
        <div className="prose-elegant mt-6 space-y-4">
          <p>
            A diagnosis of squint (strabismus) often causes anxiety and confusion. Many patients — and especially parents — are told <em>conflicting advice</em> by friends, relatives, or social media. This book replaces myths with <strong>clear, evidence-based information</strong> explained in simple language that anyone can understand.
          </p>
          <p>
            Rather than using complex medical terminology, Dr. Mishra explains every concept in a warm, reassuring, and patient-friendly manner. Through practical examples, clear explanations, and frequently-asked questions, she helps readers understand not only <em>what</em> squint is, but also <em>why</em> treatment is important and what they can realistically expect.
          </p>
          <p>
            One of the key messages throughout the book is that <strong>squint is not just a cosmetic concern</strong>. It can affect visual development, depth perception, confidence, and quality of life. Fortunately, with timely diagnosis and appropriate treatment, most patients achieve excellent outcomes.
          </p>
          <p className="italic text-[hsl(var(--foreground))]/80">
            "Knowledge empowers. Early diagnosis protects vision. And understanding is the first step toward better eye health." — Dr. Manisha Mishra
          </p>
        </div>
      </section>

      {/* INSIDE THE BOOK */}
      <section className="py-14 container-page" data-testid="book-chapters">
        <SectionHeader overline="Inside the book" title="What you will learn" />
        <div className="grid md:grid-cols-2 gap-x-10 gap-y-3 mt-8 max-w-4xl">
          {b.chapters.map((chap, i) => (
            <div key={i} className="flex gap-3 items-start" data-testid={`book-chapter-${i}`}>
              <CheckCircle2 size={18} className="text-[hsl(var(--accent))] mt-0.5 shrink-0" />
              <span className="text-[15px] leading-relaxed">{chap}</span>
            </div>
          ))}
        </div>
      </section>

      {/* WHO THIS BOOK IS FOR */}
      <section className="py-14 container-page bg-[hsl(var(--secondary))]/40 border-y border-border" data-testid="book-audience">
        <div className="container-page max-w-4xl mx-auto">
          <SectionHeader overline="Who this book is for" title="Written for real families" />
          <ul className="mt-8 grid sm:grid-cols-2 gap-4">
            {b.audience.map((a, i) => (
              <li key={i} className="flex gap-3 items-start" data-testid={`book-audience-${i}`}>
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent))] shrink-0" />
                <span className="text-[15px]">{a}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* AUTHOR + EDITOR CARDS */}
      <section className="py-16 container-page" data-testid="book-people">
        <SectionHeader overline="The people behind the book" title="Author & Editor" />
        <div className="grid md:grid-cols-2 gap-6 mt-10">
          <Link
            to={b.author.url}
            className="group p-8 rounded-3xl border border-border bg-card/70 dark:bg-white/[0.03] hover:border-[hsl(var(--primary))] transition"
            data-testid="book-author-card"
          >
            <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--accent))]">Author</div>
            <div className="font-serif text-2xl mt-2">{b.author.name}</div>
            <div className="text-sm text-muted-foreground mt-1">
              MBBS (Gold Medalist) · MS (MAMC) · DNB · FICO (Merit)
            </div>
            <p className="text-sm mt-4 text-[hsl(var(--foreground))]/85">
              Cataract, squint (strabismus) &amp; paediatric ophthalmology consultant at Shantanu Netralaya Eye Hospital, Varanasi. Senior residency at Guru Nanak Eye Centre, MAMC, under one of India's pioneers in strabismus surgery.
            </p>
            <div className="mt-5 inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-[hsl(var(--primary))] group-hover:gap-2 transition-all">
              About Dr. Manisha <ArrowRight size={12} />
            </div>
          </Link>
          <Link
            to={b.editor.url}
            className="group p-8 rounded-3xl border border-border bg-card/70 dark:bg-white/[0.03] hover:border-[hsl(var(--primary))] transition"
            data-testid="book-editor-card"
          >
            <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--accent))]">Editor</div>
            <div className="font-serif text-2xl mt-2">{b.editor.name}</div>
            <div className="text-sm text-muted-foreground mt-1">
              MBBS · MS · DNB · FICO (Merit)
            </div>
            <p className="text-sm mt-4 text-[hsl(var(--foreground))]/85">
              Cataract, cornea &amp; medical-retina specialist at Shantanu Netralaya Eye Hospital, Varanasi. Reviewed and edited the book for medical accuracy and clarity, ensuring every chapter reflects current ophthalmic best practice.
            </p>
            <div className="mt-5 inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-[hsl(var(--primary))] group-hover:gap-2 transition-all">
              About Dr. Shantanu <ArrowRight size={12} />
            </div>
          </Link>
        </div>
      </section>

      {/* CTA — BUY / CONSULT */}
      <section className="py-16 container-page" data-testid="book-cta">
        <div className="rounded-3xl border border-border bg-gradient-to-br from-[hsl(var(--primary))] to-[#0a1e33] text-white p-10 sm:p-14 text-center">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--accent))] mb-3">
            Take the next step
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">
            Get the book. Or meet Dr. Manisha in person.
          </h2>
          <p className="mt-4 max-w-xl mx-auto text-white/85 text-sm">
            If your child squints, or you or a family member has been told about squint or double vision — a 20-minute consultation with Dr. Manisha at Shantanu Netralaya, Varanasi, is the fastest way to a clear plan.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href={b.amazonUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              data-testid="book-cta-amazon-btn"
            >
              <ShoppingCart size={16} /> Order on Amazon
            </a>
            <a
              href={waLink("Hello Dr. Manisha, I would like to book a squint consultation at Shantanu Netralaya.")}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost !border-white/40 !text-white hover:!bg-white/10"
              data-testid="book-cta-consult-btn"
            >
              Book a squint consultation
            </a>
          </div>
          <div className="mt-6 text-xs text-white/60">
            {CLINIC.address}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookSquintSimplified;
